import type { InvoiceTemplate } from "@/lib/templates/types";
import Dexie, { type Table } from "dexie";
import type {
  CatalogItem,
  Customer,
  DraftInvoice,
  Invoice,
  MetaRow,
  RecurringInvoiceProfile,
  SellerProfile,
} from "./types";

/**
 * Invoice Passport — Browser IndexedDB
 * See docs/archive/04-schema-spec.md §3 for the archived spec.
 *
 * 절대 규칙: 이 DB의 모든 데이터는 사용자 디바이스 안에만 존재한다.
 *           서버에 동기화 안 함. 사용자가 export/import로 이동만 가능.
 */
export class InvoicePassportDB extends Dexie {
  sellerProfiles!: Table<SellerProfile, string>;
  customers!: Table<Customer, string>;
  catalogItems!: Table<CatalogItem, string>;
  invoices!: Table<Invoice, string>;
  drafts!: Table<DraftInvoice, string>;
  meta!: Table<MetaRow, string>;
  templates!: Table<InvoiceTemplate, string>;
  recurringProfiles!: Table<RecurringInvoiceProfile, string>;

  constructor() {
    super("invoice-passport");

    // ─────────── v1 (Phase 1) ───────────
    this.version(1).stores({
      sellerProfiles: "id, country, updatedAt",
      customers: "id, name, country, isCompany, updatedAt",
      catalogItems: "id, name, category, archived, useCount, updatedAt",
      invoices:
        "id, invoiceNumber, sellerProfileId, customerId, type, status, issueDate, [sellerProfileId+invoiceNumber]",
      drafts: "id, sellerProfileId, updatedAt",
      meta: "key",
    });

    this.version(2).stores({
      templates: "id, name, builtIn, updatedAt",
    });

    this.version(3).stores({
      licenses: null,
    });

    this.version(4).stores({
      recurringProfiles: "id, name, customerId, active, nextIssueDate, updatedAt",
    });

    // Auto timestamps
    this.sellerProfiles.hook("creating", (_pk, obj) => {
      const now = new Date();
      obj.createdAt = obj.createdAt ?? now;
      obj.updatedAt = now;
    });
    this.sellerProfiles.hook("updating", () => ({ updatedAt: new Date() }));

    this.customers.hook("creating", (_pk, obj) => {
      const now = new Date();
      obj.createdAt = obj.createdAt ?? now;
      obj.updatedAt = now;
    });
    this.customers.hook("updating", () => ({ updatedAt: new Date() }));

    this.catalogItems.hook("creating", (_pk, obj) => {
      const now = new Date();
      obj.createdAt = obj.createdAt ?? now;
      obj.updatedAt = now;
    });
    this.catalogItems.hook("updating", () => ({ updatedAt: new Date() }));

    this.invoices.hook("creating", (_pk, obj) => {
      const now = new Date();
      obj.createdAt = obj.createdAt ?? now;
      obj.updatedAt = now;
    });
    this.invoices.hook("updating", () => ({ updatedAt: new Date() }));

    this.templates.hook("creating", (_pk, obj) => {
      const now = new Date();
      obj.createdAt = obj.createdAt ?? now;
      obj.updatedAt = now;
    });
    this.templates.hook("updating", () => ({ updatedAt: new Date() }));

    this.recurringProfiles.hook("creating", (_pk, obj) => {
      const now = new Date();
      obj.createdAt = obj.createdAt ?? now;
      obj.updatedAt = now;
    });
    this.recurringProfiles.hook("updating", () => ({ updatedAt: new Date() }));
  }

  /**
   * Browser storage protection request for the local IndexedDB database.
   * Triggered manually from Settings.
   */
  async requestPersistentStorage(): Promise<boolean> {
    if ("storage" in navigator && "persist" in navigator.storage) {
      return await navigator.storage.persist();
    }
    return false;
  }

  /**
   * 현재 storage 사용량
   */
  async getStorageEstimate(): Promise<{ usage: number; quota: number }> {
    if ("storage" in navigator && "estimate" in navigator.storage) {
      const est = await navigator.storage.estimate();
      return { usage: est.usage ?? 0, quota: est.quota ?? 0 };
    }
    return { usage: 0, quota: 0 };
  }

  /**
   * 전체 데이터 삭제 — Settings > Local data의 "Delete all local data" 버튼.
   * AlertDialog의 "Type DELETE to confirm" 통과 후만 호출되어야 한다.
   */
  async wipeAll(): Promise<void> {
    await this.transaction("rw", this.tables, async () => {
      for (const table of this.tables) {
        await table.clear();
      }
    });
  }
}

export const db = new InvoicePassportDB();
